// Thin API client for donor-facing checkout flow (no auth required)

declare global {
  interface Window {
    __DONATION_KIOSK_API_URL__?: string;
  }
}

const API_URL =
  (typeof window !== 'undefined' && window.__DONATION_KIOSK_API_URL__) ||
  (typeof window !== 'undefined' ? window.location.origin : '');

/**
 * CHG-0171 — per-entity cascade block served by the /options and /config
 * endpoints. These are the *effective* donor-facing values after the
 * widget's narrow-only overrides have been applied on the backend.
 * Widget code must consume these directly; do NOT combine with
 * widget-level flags a second time.
 */
export interface CascadedDonationSettings {
  allow_recurring: boolean;
  allow_pledge: boolean;
  min_amount_minor_units: number | null;
  max_amount_minor_units: number | null;
  suggested_amounts: number[];
}

export interface CampaignOption {
  kind: 'campaign';
  id: string;
  name: string;
  description: string | null;
  slug: string | null;
  cover_image_url: string | null;
  currency: string;
  goal_amount_minor_units: number;
  raised_minor_units: number;
  progress_pct: number | null;
  start_date: string | null;
  end_date: string | null;
  cascade: CascadedDonationSettings;
}

export interface DesignationOption {
  kind: 'designation';
  id: string;
  name: string;
  description: string | null;
  slug: string | null;
  icon: string | null;
  designation_type: string;
  goal_amount_minor_units: number | null;
  raised_minor_units: number;
  goal_currency: string | null;
  display_order: number;
  cascade: CascadedDonationSettings;
}

// CHG-1637 (W2.2) shipped the unified envelope on the /options endpoint
// to match donate-page-public's shape. CHG-1657 (W3.2) wires the widget
// bundle through these fields; see envelope-constraints.ts for the pure
// helpers + server contract pointers. The legacy `campaigns` /
// `designations` arrays remain for backward compatibility — the new
// `constraints.allowed_designations` + `context_lock` are the
// authoritative gate (mirrored client-side as a UX-quality measure).

export interface WidgetEnvelopePageContext {
  context_type: string;
  subtype: string | null;
  entity_id: string | null;
  organization_id: string;
  parent_chain: string[];
  brand_variant_id: string | null;
  locale: string | null;
}

export interface WidgetEnvelopeAllowedDesignation {
  id: string;
  display_name: string;
  designation_type: string | null;
  parent_chain: string[];
}

export interface WidgetEnvelopeAmountBounds {
  min_minor_units: number | null;
  max_minor_units: number | null;
  suggested_amounts: number[];
}

export interface WidgetEnvelopeConstraints {
  allowed_designations: WidgetEnvelopeAllowedDesignation[];
  campaign_lock: string | null;
  amount_bounds: WidgetEnvelopeAmountBounds;
  provider_capabilities: Record<string, boolean> | null;
  recurring_allowed: boolean;
  pledge_allowed: boolean;
  off_season_action: 'accept' | 'redirect' | 'reject';
  off_season_redirect_url: string | null;
}

export interface WidgetEnvelopeContextLock {
  kind: 'designation' | 'campaign';
  id: string;
  display_name: string;
}

export interface WidgetOptions {
  scope_type: 'all' | 'categories' | 'campaign';
  campaigns: CampaignOption[];
  designations: DesignationOption[];
  // CHG-1637 (W2.2) — unified envelope fields. Optional so legacy fixtures
  // and pre-W2.2 backend deployments still parse cleanly. CHG-1657 (W3.2)
  // is the consumer wiring.
  page_context?: WidgetEnvelopePageContext;
  constraints?: WidgetEnvelopeConstraints;
  context_lock?: WidgetEnvelopeContextLock | null;
  org_provider_capabilities?: Record<string, boolean>;
}

export interface WidgetConfig {
  id: string;
  organization_id: string;
  name?: string;
  theme_style: 'light' | 'dark' | 'brand';
  allowed_domains: string[];
  currency: string;
  header_text?: string;
  thank_you_message?: string;
  // CHG-0171 — widget-level safety caps. Entity min/max carries the
  // business rule; widget can only narrow via max/min combination in
  // the backend cascade resolver.
  min_amount_minor_units?: number;
  max_amount_minor_units?: number;
  // CHG-0171 W2 — narrow-only overrides. These are already reflected in
  // the cascade block; widget code should read cascade.allow_recurring
  // rather than testing this flag again.
  disable_recurring?: boolean;
  disable_pledge?: boolean;
  show_donor_info?: boolean;
  show_anonymous?: boolean;
  font_family?: string;
  border_radius?: number;
  button_style?: string;
  theme_overrides?: Record<string, string>;
  inherit_org_branding?: boolean;
  scope_type?: 'all' | 'categories' | 'campaign';
  // Populated by the backend from the widget_designations join table.
  designation_ids?: string[] | null;
  campaign_id?: string;
  status?: string;
  // CHG-0171 W3 — the /config endpoint embeds /options so single-entity
  // widgets avoid a second round-trip. scope_type='all' widgets still
  // load /options separately to refresh the picker.
  options?: WidgetOptions;
}

// CHG-1689 / ITER-3798 — `DonatePageConfig` widget envelope retired
// with the donate_page_configs table drop. Public widget consumes
// per-entity endpoints directly.

export interface Designation {
  id: string;
  organization_id?: string;
  name: string;
  description?: string;
  designation_type?: string;
  suggested_amounts: number[];
  display_order: number;
  visibility?: string;
  tax_deductible?: boolean;
  status: string;
  goal_amount_minor_units?: number;
  raised_minor_units?: number;
  progress_pct?: number;
  impact_config?: Record<string, any>;
  children?: Designation[];
}

export interface OrgBranding {
  name: string;
  logo_url?: string;
  primary_color?: string;
  locale?: string;
  default_currency?: string;
  /** Organization-country default for phone capture; donor may override. */
  country?: string;
  // CHG-P — full set of currencies the org accepts. When > 1, the
  // checkout amount step renders a currency dropdown. Sourced from
  // Organization.supported_currencies; null/single-entry → no picker.
  supported_currencies?: string[];
  language?: string;
  contact_email?: string;
  phone?: string;
  // CHG-0155 P6: variant vocabulary overlay (donate_button, donation, donor,
  // etc.) so Mohseen widgets render "Sadaqah" and AloraChurch widgets render
  // "Gift" without a second fetch.
  vocabulary?: Record<string, string>;
  platform_name?: string;
  show_powered_by?: boolean;
}

export interface PaymentIntentResult {
  payment_intent_id: string;
  provider_key: string;
  status: string;
  client_secret: string | null;
  redirect_url: string | null;
}

// CHG-0463 (CHG-0438 Step 4 cleanup) — channel removed; the two-axis
// (source, payment_method) replaces it.
export type DonationSourceLite = 'widget' | 'donate-page' | 'session';
export type PaymentMethodLite = 'card' | 'paypal' | 'cash' | 'check' | 'bank-transfer' | 'in-kind' | 'bkash' | 'nagad' | 'mobile-wallet' | 'other';

/** CHG-1782 — page-context envelope sent on the public payment-intent +
 *  donation calls. The backend re-resolves the render constraints from
 *  {context_type, entity_id} and rejects an out-of-context designation
 *  BEFORE it lands in PaymentIntent.intent_metadata (which the Stripe
 *  webhook trusts when it creates the Donation). REQUIRED by the backend
 *  for public donor sources (widget / donate-page). */
export interface DonatePageContextLite {
  context_type: string;
  entity_id?: string;
  context_lock?: { kind: 'designation' | 'campaign'; id: string };
  /** @deprecated Event context is informational and rejected for money entry. */
  event_id?: string;
  designation_id?: string;
  campaign_id?: string;
  // CHG-2702 (DP-001/DP-002 SESSION) — session attribution mirror, set for
  // SESSION-context donate pages. Advisory like event_id; the authoritative
  // value is the top-level PI-request session_id.
  session_id?: string;
}

export interface CreatePaymentIntentData {
  amount_minor_units: number;
  currency: string;
  source: DonationSourceLite;
  payment_method?: PaymentMethodLite;
  organization_id: string;
  widget_id?: string;
  designation_id?: string;
  campaign_id?: string;
  /** Stable non-PII correlation across provider attempts for one checkout. */
  checkout_attempt_id?: string;
  /** Previous tenant-owned failed/canceled PaymentIntent for retry tracing. */
  retry_of_payment_intent_id?: string;
  /** Short-lived exact pledge checkout credential; never a raw pledge id. */
  pledge_contribution_token?: string;
  /** @deprecated Rejected for new intents; use SESSION + session_id. */
  event_id?: string;
  // CHG-2702 (DP-R2 / DP-001 / DP-002, SESSION) — session attribution for
  // webhook-first card donations from SESSION (giving-session) donate pages.
  // Mirrors event_id: persisted into PaymentIntent.intent_metadata + mirrored
  // into provider metadata; reconcile_from_webhook writes Donation.session_id
  // from it, and the post-create session-tally path increments the
  // GivingSession counters. Without it, webhook-first session donations
  // under-attribute (session_id NULL, counters never move).
  session_id?: string;
  provider_key?: string;
  return_url?: string;
  donate_page_context?: DonatePageContextLite;
  // CHG-1215 (BUG-148 + BUG-151) — donor identity at create-intent time.
  // Persisted on the PaymentIntent.metadata snapshot so Stripe webhook
  // reconcile + redirect-provider finalization both have donor data
  // even when the provider envelope doesn't carry it back. The widget
  // ALSO sends these on the follow-up /donations/public POST for
  // Stripe (which is what creates the canonical Donation row on the
  // happy path); transmitting them on both calls is intentional —
  // webhook + record both have authoritative donor info, the one
  // that wins is the first to create the Donation row.
  donor_name?: string;
  donor_email?: string;
  donor_phone?: string;
  donor_phone_country?: string;
  anonymous?: boolean;
}

export interface RecordDonationData {
  organization_id: string;
  widget_id?: string;
  designation_id?: string;
  campaign_id?: string;
  session_id?: string;
  amount_minor_units: number;
  currency: string;
  source: DonationSourceLite;
  payment_method?: PaymentMethodLite;
  donor_name?: string;
  donor_email?: string;
  donor_phone?: string;
  donor_phone_country?: string;
  anonymous: boolean;
  payment_intent_id: string;
  pledge_contribution_token?: string;
  metadata?: Record<string, unknown>;
  donate_page_context?: DonatePageContextLite;
}

// CHG-2813 (DRIFT-1084a) — exported so the checkout controller can mint a
// STABLE per-donation-attempt key ONCE (on payment-step entry) and reuse it
// across retries of the SAME charge, rather than this module minting a fresh
// key per HTTP call. A network-blip retry that re-sent a freshly-minted key
// made Stripe create a SECOND PaymentIntent (double-charge); a stable key
// dedupes at Stripe. Distinct donations mint distinct keys (the controller
// regenerates on a genuinely new donation), so two separate gifts never
// collide and silently dedupe.
export function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Keep the compatibility fallback UUID-shaped because the same generator is
  // also used for the validated checkout_attempt_id correlation field.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (token) => {
    const random = Math.floor(Math.random() * 16);
    const value = token === 'x' ? random : ((random & 0x3) | 0x8);
    return value.toString(16);
  });
}

// BUG-137 — surface FastAPI's `detail` field as the human-readable error
// message so consumers (widget checkout, donate-page checkout) can show the
// backend's canonical wording instead of a generic "Unable to initialize
// payment" fallback. Carry the status as a separate property so callers
// that want to special-case 4xx vs 5xx can do so without re-parsing.
export class ApiError extends Error {
  status: number;
  detail: string;
  errorCode?: string;

  constructor(status: number, detail: string, message?: string, errorCode?: string) {
    super(message ?? detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.errorCode = errorCode;
  }
}

// Exported for the same error-envelope regression tests as _extractDetail.
export function _extractErrorCode(text: string): string | undefined {
  if (!text) return undefined;
  try {
    const parsed = JSON.parse(text) as { error?: unknown };
    return typeof parsed.error === 'string' ? parsed.error : undefined;
  } catch {
    return undefined;
  }
}

// CHG-3229 — surface a meaningful message from every backend error envelope the
// API can return, not just the two it used to handle. The donation/manual 422s
// arrive as the validation envelope `{error, field_errors:[{field_id, message,
// code}], message:"Request validation failed"}` whose only useful text lives in
// field_errors[].message — the old extractor returned the generic top-level
// `message` ("Request validation failed") or fell through to "API error 422".
// We now also handle FastAPI's default `detail` array (`[{loc, msg, type}]`).
// Precedence: specific field error → string detail → detail[].msg → a non-
// generic top-level message (service-layer errors put the real reason here) →
// fallback.
const _GENERIC_VALIDATION_MESSAGES = new Set([
  'request validation failed',
  'validation error',
  'validation failed',
]);

function _cleanFieldMessage(msg: string): string {
  // Pydantic prefixes model-validator errors with "Value error, "; drop it so
  // the donor sees just the human sentence.
  return msg.replace(/^Value error,\s*/i, '').trim();
}

// Exported for unit testing (CHG-3229). Not part of the public widget API.
export function _extractDetail(text: string, fallback: string): string {
  if (!text) return fallback;
  try {
    const j = JSON.parse(text) as {
      detail?: unknown;
      message?: unknown;
      field_errors?: unknown;
    };
    // 1) Backend validation envelope: first field error's message.
    if (Array.isArray(j.field_errors)) {
      for (const fe of j.field_errors) {
        const m = (fe as { message?: unknown })?.message;
        if (typeof m === 'string' && m.trim()) return _cleanFieldMessage(m);
      }
    }
    // 2) FastAPI default: detail as a plain string.
    if (typeof j.detail === 'string' && j.detail) return j.detail;
    // 3) FastAPI default: detail as an array of {msg}.
    if (Array.isArray(j.detail)) {
      for (const d of j.detail) {
        const m = (d as { msg?: unknown })?.msg;
        if (typeof m === 'string' && m.trim()) return _cleanFieldMessage(m);
      }
    }
    // 4) Service-layer errors put the real reason in `message`; use it unless
    //    it is the generic validation banner (already handled above via
    //    field_errors when those exist).
    if (
      typeof j.message === 'string' &&
      j.message.trim() &&
      !_GENERIC_VALIDATION_MESSAGES.has(j.message.trim().toLowerCase())
    ) {
      return j.message;
    }
  } catch {
    /* not JSON — fall through */
  }
  return fallback;
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
    ...(options?.headers ? { headers: { 'Content-Type': 'application/json', ...options.headers } } : {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const detail = _extractDetail(text, `API error ${res.status}`);
    const errorCode = _extractErrorCode(text);
    if (
      errorCode === 'session_target_changed' &&
      typeof window !== 'undefined'
    ) {
      // The checkout was opened before an org admin assigned the live
      // session's target. Reload immediately so the donor sees and submits
      // the canonical fund/campaign instead of a stale selection.
      window.location.reload();
    }
    throw new ApiError(res.status, detail, undefined, errorCode);
  }
  return res.json() as Promise<T>;
}

export function fetchWidgetConfig(widgetId: string): Promise<WidgetConfig> {
  return apiFetch<WidgetConfig>(`/api/v1/public/widget/${encodeURIComponent(widgetId)}/config`);
}

// CHG-0168 P1 — capture prompt (CHG-0159 I1 backend). Returns the
// contact fields the widget should surface + which are required +
// a short rationale string for the helper text. Additive — callers
// that don't call this fall back to the pre-P1 hardcoded
// email-optional/phone-optional contract.
export interface CapturePromptResponse {
  fields: string[]; // ["email", "phone"] in presentation order
  required: string[]; // subset of fields
  required_one_of?: string[]; // at least one field from this group
  rationale: string; // short human-readable explanation
}

export function fetchCapturePrompt(req: {
  country: string;
  has_email: boolean;
  has_paired_device: boolean;
  origin: 'otp' | 'receipt' | 'onboarding' | 'donation' | 'announcement';
}): Promise<CapturePromptResponse> {
  return apiFetch<CapturePromptResponse>(`/api/v1/capture-prompt`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

// CHG-MANUALPAY — offline payment (bKash/Nagad/bank) for orgs without a gateway.
export interface ManualPaymentAccountPublic {
  id: string;
  method: string;
  label: string;
  account_number: string;
  account_name?: string | null;
  instructions?: string | null;
}

export function fetchManualPaymentAccounts(orgId: string): Promise<ManualPaymentAccountPublic[]> {
  return apiFetch<ManualPaymentAccountPublic[]>(
    `/api/v1/public/manual-payment-accounts/${encodeURIComponent(orgId)}`,
  ).catch(() => []);
}

export interface ManualDonationSubmit {
  organization_id: string;
  designation_id?: string | null;
  campaign_id?: string | null;
  // CHG-3337 — session attribution + page-context envelope + honest
  // source, mirroring the online createPaymentIntent payload. Without these
  // an approved offline gift never reaches session live surfaces.
  session_id?: string;
  /** @deprecated Rejected for new claims; derived from linked session. */
  event_id?: string;
  source?: DonationSourceLite;
  donate_page_context?: DonatePageContextLite;
  amount_minor_units: number;
  currency: string;
  manual_account_id: string;
  payment_method: string;
  transaction_ref: string;
  payer_name: string;
  donor_name?: string | null;
  donor_email?: string | null;
  donor_phone?: string | null;
  donor_phone_country?: string | null;
  anonymous?: boolean;
}

export function submitManualDonation(
  payload: ManualDonationSubmit,
): Promise<{ id: string; status: string }> {
  return apiFetch<{ id: string; status: string }>(`/api/v1/donations/manual`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** CHG-0171 W3 — fetch donor-facing options (campaigns + designations
 *  with cascade) for an embedded widget. scope_type='all' widgets
 *  render this as a picker; scoped widgets typically consume the
 *  options block embedded in /config instead. */
export function fetchWidgetOptions(widgetId: string): Promise<WidgetOptions> {
  return apiFetch<WidgetOptions>(
    `/api/v1/public/widget/${encodeURIComponent(widgetId)}/options`,
  );
}

// CHG-1689 / ITER-3798 — fetchDonatePageConfig() retired with the
// donate_page_configs table drop. Public clients consume per-entity
// public endpoints directly.

export function fetchDesignations(orgId: string, designationIds?: string[] | null): Promise<Designation[]> {
  const url = `/api/v1/designations/public?organization_id=${encodeURIComponent(orgId)}`;
  return apiFetch<Record<string, any>>(url).then((r) => {
    // Endpoint returns grouped format: { causes: [], funds: [], programs: [], projects: [] }
    // Flatten the complete hierarchy for checkout. Public designation trees
    // can reach fund -> program -> project -> milestone; walking only one
    // child level made every locked milestone checkout resolve to zero rows.
    const all: Designation[] = [];
    const seen = new Set<string>();
    const appendTree = (items: unknown): void => {
      if (!Array.isArray(items)) return;
      for (const value of items) {
        if (!value || typeof value !== 'object') continue;
        const item = value as Designation;
        if (item.id && !seen.has(item.id)) {
          seen.add(item.id);
          all.push(item);
        }
        appendTree(item.children);
      }
    };
    for (const group of ['causes', 'funds', 'programs', 'projects']) {
      appendTree(r[group]);
    }
    // Filter by IDs if scope is restricted
    if (Array.isArray(designationIds)) {
      const idSet = new Set(designationIds);
      return all.filter((d) => idSet.has(d.id));
    }
    return all;
  });
}

export function fetchOrgBranding(orgId: string): Promise<OrgBranding> {
  return apiFetch<OrgBranding>(`/api/v1/orgs/${encodeURIComponent(orgId)}/public`);
}

export function createPaymentIntent(
  data: CreatePaymentIntentData,
  // CHG-2813 (DRIFT-1084a) — STABLE per-donation-attempt idempotency key. The
  // caller (DonationCheckout) mints this ONCE per logical charge attempt (on
  // payment-step entry) and re-uses it across retries of the SAME create, so a
  // network-blip retry dedupes at Stripe instead of minting a SECOND
  // PaymentIntent / double-charging the donor. Omitted → a fresh per-call key
  // is minted (legacy behavior; safe for non-charge-creating callers but NOT
  // retry-safe — always pass a stable key from the donation flow).
  idempotencyKey?: string,
): Promise<PaymentIntentResult> {
  return apiFetch<PaymentIntentResult>('/api/v1/payment-intents/public', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey ?? generateIdempotencyKey() },
    body: JSON.stringify({
      amount_minor_units: data.amount_minor_units,
      currency: data.currency,
      source: data.source,
      payment_method: data.payment_method ?? 'card',
      organization_id: data.organization_id,
      widget_id: data.widget_id,
      designation_id: data.designation_id,
      campaign_id: data.campaign_id,
      checkout_attempt_id: data.checkout_attempt_id,
      retry_of_payment_intent_id: data.retry_of_payment_intent_id,
      pledge_contribution_token: data.pledge_contribution_token,
      // CHG-2702 (DP-R2, SESSION) — session attribution. This body is
      // enumerated explicitly, so session_id must be listed here or it is
      // silently dropped and webhook-first session-card donations never record
      // Donation.session_id (and the GivingSession counters never move).
      session_id: data.session_id,
      provider_key: data.provider_key,
      return_url: data.return_url,
      // CHG-1782 — REQUIRED by the backend for source in {widget, donate-page}.
      // This function enumerates the body explicitly (unlike recordDonation,
      // which passes `data` whole), so the field must be listed here or it is
      // silently dropped and the PI-create POST 422s.
      donate_page_context: data.donate_page_context,
      // CHG-1215 — donor identity persisted to PaymentIntent.metadata
      // so async reconcilers can resolve donor without depending on
      // the follow-up /donations/public POST.
      donor_name: data.donor_name,
      donor_email: data.donor_email,
      donor_phone: data.donor_phone,
      donor_phone_country: data.donor_phone_country,
      anonymous: data.anonymous,
    }),
  });
}

export interface PaymentIntentStatusResult {
  status: string;
  provider_key: string | null;
  amount_minor_units?: number | null;
  currency?: string | null;
  designation_id?: string | null;
  campaign_id?: string | null;
  checkout_attempt_id?: string | null;
  retry_of_payment_intent_id?: string | null;
}

export function fetchPaymentIntentStatus(intentId: string, organizationId?: string): Promise<PaymentIntentStatusResult> {
  const params = organizationId ? '?organization_id=' + encodeURIComponent(organizationId) : '';
  return apiFetch<PaymentIntentStatusResult>(
    "/api/v1/payment-intents/" + encodeURIComponent(intentId) + "/status" + params
  );
}

export function recordDonation(data: RecordDonationData): Promise<{ id: string; recognition_token?: string }> {
  return apiFetch<{ id: string; recognition_token?: string }>('/api/v1/donations/public', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': generateIdempotencyKey() },
    body: JSON.stringify(data),
  });
}

export interface CreateRecurringDonationData {
  organization_id: string;
  widget_id?: string;
  designation_id?: string;
  /** DRIFT-1203 (CHG-3142) — campaign attribution for recurring gifts, parity
   *  with the one-time createPaymentIntent path. Omitted pre-fix, so recurring
   *  campaign donations were never campaign-attributed. */
  campaign_id?: string;
  amount_minor_units: number;
  currency: string;
  frequency: string;
  donor_name?: string;
  donor_email?: string;
  /** CHG-1049 (S6) — bi-channel donor identity. */
  donor_phone?: string;
  donor_phone_country?: string;
  provider_key?: string;
  // CHG-0047 P6 — when set, server snapshots the intent's
  // schedule_template onto the new recurring row. Mutually exclusive
  // with `frequency` (the server prefers intent_key if both are sent
  // and ignores frequency in that case).
  intent_key?: string | null;
  donate_page_context?: DonatePageContextLite;
}

export interface RecurringDonationResult {
  recurring_donation_id: string;
  provider_key: string;
  status: string;
  client_secret?: string;
  redirect_url?: string;
  reference_id?: string;
}

export function createRecurringDonation(
  data: CreateRecurringDonationData
): Promise<RecurringDonationResult> {
  const body: Record<string, unknown> = {
    organization_id: data.organization_id,
    widget_id: data.widget_id,
    designation_id: data.designation_id,
    // DRIFT-1203 (CHG-3142) — campaign attribution (parity with the one-time
    // createPaymentIntent body); undefined is omitted from the JSON.
    campaign_id: data.campaign_id,
    amount_minor_units: data.amount_minor_units,
    currency: data.currency,
    frequency: data.frequency,
    donor_name: data.donor_name,
    donor_email: data.donor_email,
    // DRIFT-1198 / RDM-R09 follow-up — forward donor_phone so phone-only
    // widget donors reach the (now phone-capable) public recurring route;
    // the sibling payment-intent path already sends it.
    donor_phone: data.donor_phone,
    donor_phone_country: data.donor_phone_country,
    provider_key: data.provider_key,
    donate_page_context: data.donate_page_context,
  };
  if (data.intent_key) {
    body['intent_key'] = data.intent_key;
  }
  // CHG-0685 — Idempotency-Key header so the server dedup fires on
  // payment-failure retry. Sibling createPaymentIntent /
  // recordDonation paths have always sent this; recurring was the
  // outlier and was creating duplicate Stripe subscriptions + local
  // rows when donors retried after a card decline.
  return apiFetch<RecurringDonationResult>('/api/v1/recurring-donations/public', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': generateIdempotencyKey() },
    body: JSON.stringify(body),
  });
}

// ─── PayPal ──────────────────────────────────────────────────────────────────

export interface PaymentMethodEntry {
  provider_key: string;
  /** Donor-facing row title: the org's payment display_name when renamed,
   *  otherwise the platform donor default. */
  display_name: string;
  /** The platform's canonical label, ignoring the org name — what the row's
   *  "Pay by …" line shows so an org rename never hides which method actually
   *  moves the money. Equal to display_name when the org has not renamed it.
   *  Optional: responses from a server predating the field omit it. */
  default_display_name?: string;
  type: 'inline' | 'redirect' | 'manual' | 'sandbox' | 'offline';
  icon: string | null;
  is_default: boolean;
  supports_recurring?: boolean;
}

export interface PaymentMethodsResult {
  methods: PaymentMethodEntry[];
  // backward compat
  stripe: boolean;
  paypal: boolean;
  stripe_publishable_key?: string;
  paypal_client_id?: string;
}

// PROV-07 / Stream G — `surface` scopes the methods list to providers READY
// for the requesting donor surface (readiness channels_supported + purposes).
// Defaults to the widget surface (the embed's natural channel); the backend
// also defaults to widget when the param is absent.
export function fetchPaymentMethods(
  orgId: string,
  surface = 'widget',
): Promise<PaymentMethodsResult> {
  return apiFetch<PaymentMethodsResult>(
    `/api/v1/public/payment-methods/${encodeURIComponent(orgId)}?surface=${encodeURIComponent(surface)}`
  );
}

export interface CreatePayPalOrderData {
  organization_id: string;
  widget_id?: string;
  amount_minor_units: number;
  currency: string;
  // CHG-0463 (CHG-0438 Step 4 cleanup) — channel removed.
  source: DonationSourceLite;
  payment_method?: PaymentMethodLite;
  designation_id?: string;
  campaign_id?: string;
  donor_name?: string;
  donor_email?: string;
  /** CHG-1049 (S6) — bi-channel donor identity. */
  donor_phone?: string;
  donor_phone_country?: string;
  /** @deprecated Rejected for new orders; use SESSION + session_id. */
  event_id?: string;
  session_id?: string;
  anonymous?: boolean;
  donate_page_context?: DonatePageContextLite;
}

export interface PayPalOrderResult {
  order_id: string;
  approve_url: string;
}

export function createPayPalOrder(data: CreatePayPalOrderData): Promise<PayPalOrderResult> {
  return apiFetch<PayPalOrderResult>('/api/v1/paypal/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface PayPalCaptureResult {
  id: string;
  status: string;
  donation_id?: string;
}

export function capturePayPalOrder(orderId: string): Promise<PayPalCaptureResult> {
  return apiFetch<PayPalCaptureResult>(
    `/api/v1/paypal/orders/${encodeURIComponent(orderId)}/capture`,
    { method: 'POST' }
  );
}

export interface CreatePayPalSubscriptionData {
  organization_id: string;
  widget_id?: string;
  amount_minor_units: number;
  currency: string;
  frequency: string;
  /** CHG-1049 (S6) — bi-channel donor identity. donor_email relaxed
   *  from required to optional (was `string`, now `string | undefined`)
   *  because phone-only donors are now legal subscribers. The pre-S6
   *  PayPal subscription path used `donor_email: this.donorInfo.email
   *  || 'anonymous@example.com'` as a literal fallback to satisfy the
   *  required type — REMOVED in S6. */
  donor_email?: string;
  donor_phone?: string;
  donor_phone_country?: string;
  donor_name?: string;
  designation_id?: string;
  campaign_id?: string;
  anonymous?: boolean;
  donate_page_context?: DonatePageContextLite;
}

export interface PayPalSubscriptionResult {
  subscription_id: string;
  approve_url: string;
}

export function createPayPalSubscription(
  data: CreatePayPalSubscriptionData
): Promise<PayPalSubscriptionResult> {
  return apiFetch<PayPalSubscriptionResult>('/api/v1/paypal/subscriptions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ─── Returning Donor & Funnel Tracking ──────────────────────────────────────

export interface ReturningDonorResult {
  recognized: boolean;
  donor_state?: 'new' | 'returning' | 'lapsed' | 'recurring';
  last_amount_minor_units?: number;
  suggested_amounts?: number[];
}

export function fetchReturningDonor(
  pageId: string,
  agDonorToken: string,
  orgId: string
): Promise<ReturningDonorResult> {
  // CHG-1686 (W4.2.1): tenant scoping flows through an explicit org_id
  // query param; the endpoint no longer derives organization_id from a
  // DonatePageConfig row lookup. ``orgId`` is required — calling without
  // it yields ``{recognized: false}`` (pre-launch hard-break).
  return apiFetch<ReturningDonorResult>(
    `/api/v1/donate-pages/${encodeURIComponent(pageId)}/returning-donor?ag_donor=${encodeURIComponent(agDonorToken)}&org_id=${encodeURIComponent(orgId)}`
  );
}

export function trackFunnel(pageId: string, eventData: { event_type: string; metadata?: Record<string, any> }): Promise<void> {
  return apiFetch<void>(`/api/v1/donate-pages/${encodeURIComponent(pageId)}/track-funnel`, {
    method: 'POST',
    body: JSON.stringify(eventData),
  });
}

// CHG-0932 / ITER-C2 — donor self-create pledge (program plan §5.4)
// POST /api/v1/public/pledges. Mirrors the server's
// PledgeCreatePublicRequest / PledgeCreatePublicResponse shape from
// ref:tier-4-rest-api-spec/pledge endpoint P-14.

export interface PledgePublicCreateInput {
  widget_id?: string;
  organization_id: string;
  designation_id?: string | null;
  campaign_id?: string | null;
  total_amount_minor_units: number;
  currency: string;
  frequency: 'one_time' | 'monthly' | 'quarterly' | 'annual';
  intent_key?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  donor: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    phone_country?: string | null;
    anonymous?: boolean;
  };
  session_id?: string | null;
  source_channel?: string;
  donate_page_context?: DonatePageContextLite;
}

export interface PledgePublicCreateResult {
  pledge_id: string;
  donor_id: string;
  donor_auto_created: boolean;
  status: string;
  total_amount_minor_units: number;
  currency: string;
  frequency: string;
  intent_key: string | null;
  schedule_expression: Record<string, any> | null;
  next_reminder_at: string | null;
  created_at: string;
}

export function createPublicPledge(
  data: PledgePublicCreateInput,
  // CHG-3027 / PLG-R01a — STABLE per-pledge-attempt idempotency key. The
  // caller (DonationCheckout) mints this ONCE per logical pledge attempt and
  // re-uses it across retries of the SAME create, so a network-blip / transient
  // 5xx retry dedupes server-side (once the public route honors it in R01c)
  // instead of creating a second Pledge. Omitted → a fresh per-call key is
  // minted (legacy behavior; not retry-safe — always pass a stable key from the
  // pledge flow). Forward-compatible: backends that don't yet read the header
  // ignore it harmlessly.
  idempotencyKey?: string,
): Promise<PledgePublicCreateResult> {
  return apiFetch<PledgePublicCreateResult>('/api/v1/public/pledges', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey ?? generateIdempotencyKey(),
    },
    body: JSON.stringify(data),
  });
}
